import { Box, Container, Typography } from '@mui/material';
import { RSMC_PILLARS } from '../constants/rsmcPillars';

/**
 * Renders a pillar title with its leading letter emphasized in brand green,
 * so the four titles together spell out the RSMC acronym.
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Pillar title
 * @param {Object} [props.sx] - Additional MUI sx styling for the wrapper
 * @returns {JSX.Element} Title with highlighted first letter
 */
function PillarTitle({ title, sx = {} }) {
  return (
    <Typography component="span" sx={{ whiteSpace: 'nowrap', ...sx }}>
      <Box
        component="span"
        sx={{ color: 'primary.main', fontWeight: 700, fontSize: '1.15em' }}
      >
        {title.charAt(0)}
      </Box>
      {title.slice(1)}
    </Typography>
  );
}

/**
 * RsmcPillarsStrip - Dark full-width strip listing the four RSMC pillars
 * on a single line, shown directly under the home page hero.
 *
 * @returns {JSX.Element} Pillars strip component
 */
export function RsmcPillarsStrip() {
  return (
    <Box
      sx={{
        backgroundColor: 'var(--color-secondary)',
        borderBottom: '3px solid',
        borderBottomColor: 'primary.main',
        py: { xs: 2.5, md: 3.5 },
        px: 2,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        columnGap: { xs: 2, md: 4 },
        rowGap: 1.25,
      }}
    >
      {RSMC_PILLARS.map((pillar, index) => (
        <Box
          key={pillar.title}
          sx={{
            display: 'flex',
            alignItems: 'center',
            columnGap: { xs: 2, md: 4 },
          }}
        >
          {index > 0 && (
            <Typography
              component="span"
              aria-hidden="true"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                display: { xs: 'none', sm: 'inline' },
              }}
            >
              •
            </Typography>
          )}
          <PillarTitle
            title={pillar.title}
            sx={{
              color: 'var(--color-secondary-text)',
              fontWeight: 500,
              fontSize: { xs: '1.05rem', md: '1.60rem' },
              letterSpacing: '0.05em',
              fontFamily: '"Playfair Display", "Roboto", sans-serif',
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

/**
 * RsmcPillarsSection - Four cards expanding on the RSMC pillars, displayed
 * above the Solutions & Services section. Mirrors the ServiceCard styling
 * (white cards with alternating green corner accents).
 *
 * @returns {JSX.Element} Pillars card section
 */
export function RsmcPillarsSection() {
  return (
    <Box sx={{ backgroundColor: 'var(--color-background-light)' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          {RSMC_PILLARS.map((pillar, index) => {
            const isEven = index % 2 === 0;
            return (
              <Box
                key={pillar.title}
                sx={{
                  backgroundColor: 'var(--color-card)',
                  position: 'relative',
                  p: 3,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  borderRadius: '12px',
                  border: '1px solid var(--color-border)',
                  display: 'flex',
                  flexDirection: 'column',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -1,
                    left: isEven ? -1 : 'auto',
                    right: !isEven ? -1 : 'auto',
                    width: '50px',
                    height: '50px',
                    borderTop: '3px solid',
                    borderTopColor: 'primary.main',
                    borderLeft: isEven ? '3px solid' : 'none',
                    borderRight: !isEven ? '3px solid' : 'none',
                    borderLeftColor: 'primary.main',
                    borderRightColor: 'primary.main',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -1,
                    right: isEven ? -1 : 'auto',
                    left: !isEven ? -1 : 'auto',
                    width: '50px',
                    height: '50px',
                    borderBottom: '3px solid',
                    borderBottomColor: 'primary.main',
                    borderRight: isEven ? '3px solid' : 'none',
                    borderLeft: !isEven ? '3px solid' : 'none',
                    borderRightColor: 'primary.main',
                    borderLeftColor: 'primary.main',
                  },
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <PillarTitle
                  title={pillar.title}
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '1.25rem',
                    mb: 1,
                    display: 'block',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'var(--color-text-muted)',
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                  }}
                >
                  {pillar.description}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
}

/**
 * RsmcBadgesStrip - Dark full-width strip below the pillar cards.
 * Intentionally blank for now; partner/AWS badges will land here.
 *
 * @returns {JSX.Element} Empty badges strip placeholder
 */
export function RsmcBadgesStrip() {
  return (
    <Box
      sx={{
        backgroundColor: 'var(--color-secondary)',
        borderTop: '3px solid',
        borderTopColor: 'primary.main',
        borderBottom: '3px solid',
        borderBottomColor: 'primary.main',
        minHeight: '72px',
      }}
    />
  );
}
